import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { checkRightHandSideExperimentEnabled } from 'components/Core/Payment/Growth/RightHandSideExperiment/utils/rightHandSideExperiment';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { insightSelectors } from 'store/insight/selectors';
import { RootState } from 'store/store';

// Type definitions consolidated from existing utilities
export interface ConvenienceFeeConditions {
  isBreakdownExpanded: boolean;
}

export interface InvoiceNumberDisplayConfig {
  shouldShowInvoiceLineItems: boolean;
}

export interface BreakdownSectionConfig {
  onToggleBreakdown?: () => void;
}

export interface ShouldShowInvoiceLineItemsConfig {
  isBreakdownExpanded: boolean;
}

/**
 * Centralized hook for RHS experiment logic and conditional rendering utilities.
 */
export const useRHSExperiment = () => {
  // Get data from Redux
  const sale = useSelector((state: RootState) => state.sale);
  const insight = useSelector((state: RootState) => state.insight);
  const growthRHSTreatmentKey = insightSelectors.growthRightHandSideTreatmentKeySelector(insight);
  const isFullyPaid = insightSelectors.isFullyPaidSelector(insight);

  // Get mobile state using established pattern
  const isMobile = useIsMobileScreen(768);

  // Calculate experiment state using existing logic
  const experimentState = useMemo(() => {
    return checkRightHandSideExperimentEnabled(growthRHSTreatmentKey, sale, isMobile);
  }, [growthRHSTreatmentKey, sale, isMobile]);

  // Extract experiment flags for easier access
  const {
    isRHSExperiment,
    isRHSExperimentControl,
    isRHSExperimentLineItemsEnabled,
    isRHSExperimentBreakdownButtonEnabled,
  } = experimentState;

  // Convenience fee total logic (from breakdownUtils.ts)
  const shouldShowConvenienceFeeTotal = useCallback(
    ({ isBreakdownExpanded }: ConvenienceFeeConditions): boolean => {
      if (!isFullyPaid) return false;

      // On mobile, breakdown button experiment doesn't exist, so always show convenience fee Total
      if (isMobile) {
        return true;
      }

      // Desktop: Hide in breakdown experiment when collapsed, show when expanded or not in experiment
      return !isRHSExperimentBreakdownButtonEnabled || isBreakdownExpanded;
    },
    [isFullyPaid, isMobile, isRHSExperimentBreakdownButtonEnabled]
  );

  // Breakdown section rendering logic (from breakdownUtils.ts)
  const shouldShowBreakdownSection = useCallback(
    ({ onToggleBreakdown }: BreakdownSectionConfig): boolean => {
      // Disable experiment entirely on mobile to preserve mobile experience
      if (isMobile) {
        return false;
      }

      return isRHSExperimentBreakdownButtonEnabled && !!onToggleBreakdown;
    },
    [isMobile, isRHSExperimentBreakdownButtonEnabled]
  );

  // Breakdown total logic (from breakdownUtils.ts)
  const shouldShowBreakdownTotal = useCallback(
    (isBreakdownExpanded: boolean): boolean => {
      return isRHSExperimentBreakdownButtonEnabled && isBreakdownExpanded;
    },
    [isRHSExperimentBreakdownButtonEnabled]
  );

  // Invoice number ID value logic (from InvoiceSummary.tsx)
  const getInvoiceNumberIdValue = useCallback(
    ({ shouldShowInvoiceLineItems }: InvoiceNumberDisplayConfig): string => {
      // Mobile: always use original logic
      if (isMobile) {
        return shouldShowInvoiceLineItems
          ? 'INVOICE_SUMMARY_INVOICE_NUMBER'
          : 'INVOICE_SUMMARY_INVOICE';
      }

      // Non-mobile: experiment-specific logic
      if (isRHSExperimentLineItemsEnabled) {
        // LineItems treatment: use original logic
        return shouldShowInvoiceLineItems
          ? 'INVOICE_SUMMARY_INVOICE_NUMBER'
          : 'INVOICE_SUMMARY_INVOICE';
      }

      // BreakdownButton, Control, or no experiment: always "INVOICE_SUMMARY_INVOICE"
      return 'INVOICE_SUMMARY_INVOICE';
    },
    [isMobile, isRHSExperimentLineItemsEnabled]
  );

  // Invoice number label logic (from InvoiceSummary.tsx)
  const getInvoiceNumberLabel = useCallback(
    ({ shouldShowInvoiceLineItems }: InvoiceNumberDisplayConfig): string => {
      // Mobile: always use original logic
      if (isMobile) {
        return shouldShowInvoiceLineItems ? 'Number' : 'Invoice';
      }

      // Non-mobile: experiment-specific logic
      if (isRHSExperimentLineItemsEnabled) {
        // LineItems treatment: use original logic
        return shouldShowInvoiceLineItems ? 'Number' : 'Invoice';
      }

      // BreakdownButton, Control, or no experiment: always "Invoice"
      return 'Invoice';
    },
    [isMobile, isRHSExperimentLineItemsEnabled]
  );

  // Calculate shouldShowInvoiceLineItems based on experiment state and expansion
  const shouldShowInvoiceLineItems = useMemo(() => {
    if (isRHSExperimentControl) {
      return false; // Control: keep current experience
    } else if (isRHSExperimentLineItemsEnabled) {
      return true; // LineItems: show line items directly (includes mobile conversion)
    } else if (isRHSExperimentBreakdownButtonEnabled) {
      return false; // BreakdownButton: depends on expansion state (see calculateShouldShowInvoiceLineItems)
    }
    return false; // No experiment
  }, [
    isRHSExperimentControl,
    isRHSExperimentLineItemsEnabled,
    isRHSExperimentBreakdownButtonEnabled,
  ]);

  // Function to calculate shouldShowInvoiceLineItems based on breakdown expansion
  const calculateShouldShowInvoiceLineItems = useCallback(
    ({ isBreakdownExpanded }: ShouldShowInvoiceLineItemsConfig): boolean => {
      if (isRHSExperimentControl) {
        return false; // Control: keep current experience
      } else if (isRHSExperimentLineItemsEnabled) {
        return true; // LineItems: show line items directly (includes mobile conversion)
      } else if (isRHSExperimentBreakdownButtonEnabled) {
        return isBreakdownExpanded; // BreakdownButton: show only when expanded (desktop only)
      }
      return false; // No experiment
    },
    [isRHSExperimentControl, isRHSExperimentLineItemsEnabled, isRHSExperimentBreakdownButtonEnabled]
  );

  return {
    // Experiment state flags
    isRHSExperiment,
    isRHSExperimentControl,
    isRHSExperimentLineItemsEnabled,
    isRHSExperimentBreakdownButtonEnabled,

    // Computed boolean for common case (when breakdown state doesn't matter)
    shouldShowInvoiceLineItems,

    // Utility functions that components can call with their specific data
    shouldShowConvenienceFeeTotal,
    shouldShowBreakdownSection,
    shouldShowBreakdownTotal,
    getInvoiceNumberIdValue,
    getInvoiceNumberLabel,
    calculateShouldShowInvoiceLineItems,

    // Note: isMobile removed for better separation of concerns
    // Components should use useIsMobileScreen directly
  };
};
